/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * Shared helpers for the Web Push APIs:
 *  - urlBase64ToUint8Array: decode a URL-safe base64 VAPID public key into
 *    the Uint8Array that pushManager.subscribe expects as applicationServerKey.
 *  - arrayBufferToBase64Url: encode the p256dh / auth keys returned by
 *    subscription.getKey(...) into the URL-safe base64 strings the backend
 *    wants to store (matches Python's base64.urlsafe_b64encode without padding).
 *
 * Kept dependency-free so both the React bundle and the service worker bundle
 * can import it.
 */

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
    if (!base64String) {
        // A standard VAPID public key is 87 base64url chars (= 65 bytes after
        // decode: uncompressed EC point). An empty input means the key is
        // not configured — return an empty array and let the caller decide
        // how to surface it (we'd otherwise throw from atob).
        return new Uint8Array();
    }
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    let rawData: string;
    try {
        rawData =
            typeof atob === 'function'
                ? atob(base64)
                : // Fallback for environments where atob is not global (e.g. older node).
                  Buffer.from(base64, 'base64').toString('binary');
    } catch (_err) {
        // atob throws InvalidCharacterError on malformed base64. Treat it
        // like an empty key — caller sees zero-length applicationServerKey
        // and PushManager.subscribe will reject with a readable error.
        return new Uint8Array();
    }
    const output = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        output[i] = rawData.charCodeAt(i);
    }
    return output;
}

export function arrayBufferToBase64Url(buf: ArrayBuffer | null): string {
    if (!buf) return '';
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = typeof btoa === 'function' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
